import './ManageBotSeatingMenu.styles.scss';

import { observer } from 'mobx-react';
import { PrimeIcons } from 'primereact/api';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { useDebounce } from 'primereact/hooks';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Menubar } from 'primereact/menubar';
import React, { forwardRef, useEffect, useMemo } from 'react';

import { LobbyTableCustomSortingProperty } from '../../../model/lobbytable/LobbyTableCustomSorting';
import commonStore from '../../../store/CommonStore';
import lobbyTableStore from '../../../store/LobbyTableStore';
import userSettingsStore from '../../../store/UserSettingsStore';
import FilterMenuItem from '../../shared/FilterMenuItem/FilterMenuItem';
import { MenuItem } from '../../shared/FilterMenuItem/FilterMenuItem.types';
import SortingInput from '../../shared/SortingInput/SortingInput';
import { ManageBotSeatingMenuProps } from './ManageBotSeatingMenu.types';

const ManageBotSeatingMenu = forwardRef<
  HTMLDivElement,
  ManageBotSeatingMenuProps
>(({ filterOptions, selectedClub }, ref) => {
  const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce(
    lobbyTableStore.searchFilter.searchName,
    400,
  );

  useEffect(() => {
    if (lobbyTableStore.searchFilter.searchName !== debouncedSearchValue) {
      lobbyTableStore.updateFilterOptions({ searchName: debouncedSearchValue });
    }
  }, [debouncedSearchValue]);

  const items: MenuItem[] = useMemo(() => {
    if (selectedClub && userSettingsStore.settings.table.expertMode) {
      return [];
    }
    return [
      {
        label: 'Super League',
        icon: 'pi pi-trophy',
        options: filterOptions.superLeagues,
        value: lobbyTableStore.searchFilter.superLeagues,
        sorted: true,
        onChange: (newValue: string[]) =>
          lobbyTableStore.updateFilterOptions({ superLeagues: newValue }),
        template: item => <FilterMenuItem item={item} />,
      },
      {
        label: 'League',
        icon: 'pi pi-trophy',
        options: filterOptions.leagues,
        value: lobbyTableStore.searchFilter.leagues,
        sorted: true,
        onChange: (newValue: string[]) =>
          lobbyTableStore.updateFilterOptions({ leagues: newValue }),
        template: item => <FilterMenuItem item={item} />,
      },
      {
        label: 'Club',
        icon: 'pi pi-users',
        options: filterOptions.clubs,
        value: lobbyTableStore.searchFilter.clubs,
        sorted: true,
        onChange: (newValue: string[]) =>
          lobbyTableStore.updateFilterOptions({ clubs: newValue }),
        template: item => <FilterMenuItem item={item} />,
      },
      {
        label: 'Game Type',
        icon: 'pi pi-list',
        options: filterOptions.gameTypes,
        value: lobbyTableStore.searchFilter.gameTypes,
        sorted: false,
        onChange: (newValue: string[]) =>
          lobbyTableStore.updateFilterOptions({ gameTypes: newValue }),
        template: item => <FilterMenuItem item={item} />,
      },
      {
        label: 'Limit',
        icon: 'pi pi-dollar',
        options: filterOptions.limits.map(limit =>
          commonStore.convertLimit(limit).toString(),
        ),
        value: lobbyTableStore.searchFilter.limits.map(limit =>
          commonStore.convertLimit(limit).toString(),
        ),
        sorted: true,
        onChange: (newValue: string[]) =>
          lobbyTableStore.updateFilterOptions({
            limits: newValue.map(limit =>
              commonStore.revertLimit(Number(limit)),
            ),
          }),
        template: item => <FilterMenuItem item={item} />,
      },
      {
        label: 'Available for sit',
        template: item => (
          <>
            <div className="d-flex align-items-center only-available">
              <label className="me-2" htmlFor="only-available">
                {item.label}
              </label>
              <Checkbox
                inputId="only-available"
                checked={lobbyTableStore.searchFilter.onlyAvailable}
                onChange={e =>
                  lobbyTableStore.updateFilterOptions({
                    onlyAvailable: e.checked,
                  })
                }
              />
            </div>
          </>
        ),
      },
    ];
  }, [
    lobbyTableStore.searchFilter,
    filterOptions,
    userSettingsStore.settings.table.limsX100,
  ]);

  const resetFilter = (): void => {
    lobbyTableStore.resetFilter();
    setSearchValue('');
  };

  const end = (
    <div className="d-flex gap-3">
      <SortingInput
        sessionStorageKey="lobby-tables-sorting"
        sortingOptionsEnum={LobbyTableCustomSortingProperty}
        selectedSortProperty={lobbyTableStore.sorting.sortProperty}
        sortDirection={lobbyTableStore.sorting.sortDirection}
        onSortDirectionChanged={lobbyTableStore.setSortDirection}
        onSelectedSortPropertyChanged={lobbyTableStore.setSortProperty}
      />
      <div className="search d-flex gap-1">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search"> </InputIcon>
          <InputText
            placeholder="Search by Name or ID"
            type="text"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
          />
        </IconField>
        <Button
          icon={PrimeIcons.TIMES_CIRCLE}
          text
          rounded
          severity="danger"
          tooltip="Clear all filters"
          tooltipOptions={{ position: 'bottom' }}
          onClick={resetFilter}
        />
      </div>
    </div>
  );

  return (
    <div ref={ref} className="ManageBotSeatingMenu-Component mb-5">
      <Menubar model={items} end={end} />
    </div>
  );
});

export default observer(ManageBotSeatingMenu);
